import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useIdleTimeout Hook
 * 
 * Tracks user activity and triggers callbacks for session timeout warnings and logout.
 * 
 * SECURITY FEATURES:
 * - 15-minute idle timeout for HIPAA compliance
 * - Warning modal shown 1 minute before timeout
 * - Automatic logout on timeout
 * - Activity tracking: mouse, keyboard, scroll, touch
 * 
 * @param {Object} options
 * @param {Function} options.onWarning - Called 1 minute before timeout
 * @param {Function} options.onTimeout - Called when session times out
 * @param {boolean} options.enabled - Whether timeout tracking is active
 * @param {number} options.timeoutMs - Total timeout in milliseconds (default: 15 minutes)
 * @param {number} options.warningMs - Warning before timeout in milliseconds (default: 1 minute)
 */

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;      // 15 minutes
const DEFAULT_WARNING_MS = 1 * 60 * 1000;       // 1 minute before timeout
const THROTTLE_MS = 1000;                        // Throttle activity events to 1 per second

export const useIdleTimeout = ({
    onWarning,
    onTimeout,
    enabled = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    warningMs = DEFAULT_WARNING_MS
}) => {
    const timeoutRef = useRef(null);
    const warningRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const countdownRef = useRef(null);

    /**
     * Clear all active timers
     */
    const clearTimers = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (warningRef.current) {
            clearTimeout(warningRef.current);
            warningRef.current = null;
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    /**
     * Start countdown timer for warning modal
     */
    const startCountdown = useCallback(() => {
        const warningDurationSeconds = Math.floor(warningMs / 1000);
        setSecondsRemaining(warningDurationSeconds);
        
        countdownRef.current = setInterval(() => {
            setSecondsRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [warningMs]);

    /**
     * Reset all timers - called on user activity
     */
    const resetTimer = useCallback(() => {
        if (!enabled) return;

        clearTimers();
        setIsWarningVisible(false);
        lastActivityRef.current = Date.now();

        // Set warning timer (fires 1 minute before timeout)
        const warningDelay = timeoutMs - warningMs;
        warningRef.current = setTimeout(() => {
            setIsWarningVisible(true);
            startCountdown();
            if (onWarning) {
                onWarning();
            }
        }, warningDelay);

        // Set timeout timer (fires at full timeout)
        timeoutRef.current = setTimeout(() => {
            setIsWarningVisible(false);
            if (onTimeout) {
                onTimeout();
            }
        }, timeoutMs);
    }, [enabled, timeoutMs, warningMs, onWarning, onTimeout, clearTimers, startCountdown]);

    /**
     * Handle user activity to stay logged in
     * Called when user clicks "Stay Logged In" on warning modal
     */
    const stayLoggedIn = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    /**
     * Get time remaining until timeout
     */
    const getTimeRemaining = useCallback(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remaining = Math.max(0, timeoutMs - elapsed);
        return {
            total: remaining,
            minutes: Math.floor(remaining / 60000),
            seconds: Math.floor((remaining % 60000) / 1000)
        };
    }, [timeoutMs]);

    /**
     * Set up activity listeners and initialize timer
     */
    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return;
        }

        // Events that indicate user activity
        const activityEvents = [
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
            'mousemove',
            'click'
        ];

        // Throttled activity handler
        let lastThrottleTime = 0;
        const handleActivity = () => {
            const now = Date.now();
            // Only reset if enough time has passed (throttle)
            if (now - lastThrottleTime > THROTTLE_MS) {
                lastThrottleTime = now;
                // Only reset if warning is not visible, or if it is, user needs to click button
                if (!isWarningVisible) {
                    resetTimer();
                }
            }
        };

        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, handleActivity, { passive: true });
        });

        // Start the initial timer
        resetTimer();

        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
            clearTimers();
        };
    }, [enabled, resetTimer, clearTimers, isWarningVisible]);

    return {
        resetTimer,
        stayLoggedIn,
        getTimeRemaining,
        isWarningVisible,
        secondsRemaining,
        clearTimers
    };
};

export default useIdleTimeout;

